import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

type TFilterPillsBarProps = {
  children: ReactNode;
  /** Rendered inside the card, below the scrollable pill row. */
  footer?: ReactNode;
  className?: string;
};

/**
 * Shared pill bar container matching MyDealStackFeature deal-stage style.
 * Use for category filters, stage filters, or any horizontal pill row.
 */
export function FilterPillsBar({ children, footer, className = "" }: TFilterPillsBarProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  const updateFades = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeftFade(el.scrollLeft > 4);
    setShowRightFade(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateFades();
    const ro = new ResizeObserver(updateFades);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateFades]);

  return (
    <div
      className={`relative rounded-xl border border-border bg-card p-3 shadow-sm sm:rounded-2xl sm:p-4 ${className}`.trim()}
    >
      <div className="relative">
        <div
          ref={scrollRef}
          onScroll={updateFades}
          className="flex items-center gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [touch-action:pan-x] [&::-webkit-scrollbar]:hidden"
        >
          {children}
        </div>
        {showLeftFade && (
          <div className="pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-card to-transparent" />
        )}
        {showRightFade && (
          <div className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-card to-transparent" />
        )}
      </div>
      {footer}
    </div>
  );
}
